import * as PIXI from 'pixi.js';
import { CARD_WIDTH, CARD_HEIGHT, getProxiedCardImageUrl } from '../../utils';
import Tappable from './Tappable';

const MISSING_CARD_ALPHA = 0.45;

class LoadingCard extends PIXI.Container {
    constructor(scaleFactor) {
        super();
        this.label = 'loadingCard';

        const cardMask1 = new PIXI.Graphics();
        cardMask1.roundRect(0, 0, CARD_WIDTH * scaleFactor, CARD_HEIGHT * scaleFactor, 17);
        cardMask1.fill({ color: '#ffffff' });
        this.addChild(cardMask1);

        const cardMask2 = new PIXI.Graphics();
        cardMask2.roundRect(0, 0, CARD_WIDTH * scaleFactor, CARD_HEIGHT * scaleFactor, 17);
        cardMask2.fill({ color: '#ffffff' });
        this.addChild(cardMask2);

        const loaderBgGraphics = new PIXI.Graphics();
        loaderBgGraphics.rect(0, 0, CARD_WIDTH * scaleFactor, CARD_HEIGHT * scaleFactor);
        loaderBgGraphics.fill({ color: '#192333' });
        loaderBgGraphics.mask = cardMask1;
        this.addChild(loaderBgGraphics);

        const gradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 1, y: 0 },
            colorStops: [
                { offset: 0, color: 'rgba(255, 255, 255, 0)' },
                { offset: 0.5, color: 'rgba(255, 255, 255, 0.15)' },
                { offset: 1, color: 'rgba(255, 255, 255, 0)' },
            ],
        });

        const shimmerWidth = 150 * scaleFactor;

        const shimmerLine = new PIXI.Graphics();
        shimmerLine.rect(0, -CARD_HEIGHT * scaleFactor, shimmerWidth, CARD_HEIGHT * scaleFactor * 3);
        shimmerLine.fill(gradient);
        shimmerLine.mask = cardMask2;
        shimmerLine.angle = 30;
        shimmerLine.x = -300 * scaleFactor;
        this.addChild(shimmerLine);

        this.shimmerMoveFunction = ticker => {
            loaderBgGraphics.x = 0;

            shimmerLine.x += ticker.deltaTime * 6.5 * scaleFactor;
            if (shimmerLine.x > 400 * scaleFactor) {
                shimmerLine.x = -300 * scaleFactor;
            }
        };

        PIXI.Ticker.shared.add(this.shimmerMoveFunction);
    }

    destroy() {
        PIXI.Ticker.shared.remove(this.shimmerMoveFunction);
        super.destroy();
    }
}

export default class TappableCard extends Tappable {
    constructor(card, opts = {}) {
        super();
        this.card = card;
        const { scaleFactor = 1, countersMode = 'none', selected = 0, available = 0, isLeaderBackface = false, atLimit = false, showAsMissing = false } = opts;
        this.scaleFactor = scaleFactor;

        if (isLeaderBackface) {
            const texture = PIXI.Assets.get('leaderBackface');
            const cardSprite = new PIXI.Sprite(texture);
            cardSprite.alpha = showAsMissing ? MISSING_CARD_ALPHA : 1;
            cardSprite.layout = {
                width: CARD_WIDTH * scaleFactor,
                height: CARD_HEIGHT * scaleFactor,
            };
            this.addChild(cardSprite);
        } else if (card) {
            const loadingCard = new LoadingCard(scaleFactor);
            loadingCard.layout = {
                width: CARD_WIDTH * scaleFactor,
                height: CARD_HEIGHT * scaleFactor,
                transformOrigin: 'center',
            };
            this.addChild(loadingCard);

            this.onHoldStart = () => {
                loadingCard.scale.set(0.9);
            };

            this.onHoldEnd = () => {
                loadingCard.scale.set(1);
            };

            PIXI.Assets.load([
                { src: getProxiedCardImageUrl(card.imgUrl) },
            ]).then(() => {
                const texture = PIXI.Assets.get(getProxiedCardImageUrl(card.imgUrl));
                const cardSprite = new PIXI.Sprite(texture);
                cardSprite.alpha = showAsMissing ? MISSING_CARD_ALPHA : 1;
                cardSprite.layout = {
                    width: CARD_WIDTH * this.scaleFactor,
                    height: CARD_HEIGHT * this.scaleFactor,
                };
                this.addChild(cardSprite);

                loadingCard.destroy();

                this.onHoldStart = () => {
                    cardSprite.layout.setStyle({
                        width: CARD_WIDTH * this.scaleFactor * 0.9,
                        height: CARD_HEIGHT * this.scaleFactor * 0.9,
                    });
                };

                this.onHoldEnd = () => {
                    cardSprite.layout.setStyle({
                        width: CARD_WIDTH * this.scaleFactor,
                        height: CARD_HEIGHT * this.scaleFactor,
                    });
                };
            });
        }

        this.onTap = () => {
            this.onCardTap?.(this.card);
        };

        this.layout = {
            width: CARD_WIDTH * scaleFactor,
            height: CARD_HEIGHT * scaleFactor,
            justifyContent: 'center',
            alignItems: 'center',
        };

        if (countersMode === 'editor') {
            const selectionContainer = new PIXI.Container();
            selectionContainer.label = 'selectionContainer';
            selectionContainer.layout = {
                width: CARD_WIDTH * scaleFactor * 0.45,
                height: CARD_HEIGHT * scaleFactor * 0.12,
                position: 'absolute',
                bottom: 0,
                left: 0,
            };
            selectionContainer.zIndex = 10;
            this.addChild(selectionContainer);

            const isMaxSelected = selected >= available || atLimit;
            let fillColor, strokeColor, textColor;
            if (!isMaxSelected) {
                fillColor = '#1e1508';
                strokeColor = '#59411f';
                textColor = '#e4b45b';
            } else {
                fillColor = '#780505';
                strokeColor = '#c8412b';
                textColor = '#ee7a40';
            }

            const selectionGraphics = new PIXI.Graphics();
            selectionGraphics.rect(0, 0, CARD_WIDTH * scaleFactor * 0.45, CARD_HEIGHT * scaleFactor * 0.12);
            selectionGraphics.fill({ color: fillColor });
            selectionGraphics.stroke({ color: strokeColor, width: 5 });
            selectionContainer.addChild(selectionGraphics);

            const selectionText = new PIXI.Text({
                text: `${selected}/${available}`,
                style: {
                    fontFamily: 'Arial',
                    fontSize: 30 * scaleFactor,
                    fill: textColor,
                    fontWeight: '600',
                },
            });
            selectionText.label = 'selectionText';
            selectionText.x = (CARD_WIDTH * scaleFactor * 0.45) / 2;
            selectionText.y = (CARD_HEIGHT * scaleFactor * 0.12) / 2;
            selectionText.anchor.set(0.5);
            selectionContainer.addChild(selectionText);
        } else if (countersMode === 'collection') {
            const selectionContainer = new PIXI.Container();
            selectionContainer.label = 'selectionContainer';
            selectionContainer.layout = {
                width: CARD_WIDTH * scaleFactor * 0.45,
                height: CARD_HEIGHT * scaleFactor * 0.12,
                position: 'absolute',
                bottom: 0,
                left: 0,
            };
            selectionContainer.zIndex = 10;
            this.addChild(selectionContainer);

            const selectionGraphics = new PIXI.Graphics();
            selectionGraphics.rect(0, 0, CARD_WIDTH * scaleFactor * 0.45, CARD_HEIGHT * scaleFactor * 0.12);
            selectionGraphics.fill({ color: '#1e1508' });
            selectionGraphics.stroke({ color: '#59411f', width: 5 });
            selectionContainer.addChild(selectionGraphics);

            const selectionText = new PIXI.Text({
                text: `${available}`,
                style: {
                    fontFamily: 'Arial',
                    fontSize: 30 * scaleFactor,
                    fill: '#e4b45b',
                    fontWeight: '600',
                },
            });
            selectionText.label = 'selectionText';
            selectionText.x = (CARD_WIDTH * scaleFactor * 0.45) / 2;
            selectionText.y = (CARD_HEIGHT * scaleFactor * 0.12) / 2;
            selectionText.anchor.set(0.5);
            selectionContainer.addChild(selectionText);
        }
    }

    setScaleFactor(scaleFactor) {
        this.scaleFactor = scaleFactor;
        const width = CARD_WIDTH * scaleFactor;
        const height = CARD_HEIGHT * scaleFactor;
        this.layout.setStyle({ width, height });

        for (const child of this.children) {
            if (!child.layout) continue;
            if (child.label === 'selectionContainer') {
                const badgeWidth = width * 0.45;
                const badgeHeight = height * 0.12;
                child.layout.setStyle({ width: badgeWidth, height: badgeHeight });
                const badge = child.children.find((node) => node instanceof PIXI.Graphics);
                if (badge) {
                    badge.layout = { width: badgeWidth, height: badgeHeight };
                }
                const text = child.getChildByLabel('selectionText');
                if (text) {
                    text.style.fontSize = 30 * scaleFactor;
                    text.x = badgeWidth / 2;
                    text.y = badgeHeight / 2;
                }
            } else {
                child.layout.setStyle({ width, height });
            }
        }
    }
}
